import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list.
 * When a pagination block is present (data-has-pagination),
 * disables infinite scroll and supports a "Load more" button.
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {number} */
  #loadedUpToPage = 1;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('data-has-pagination')) {
      // Disable auto-scroll infinite loading — pagination UI handles navigation
      this.infinityScrollObserver?.disconnect();

      const paginationBlock = /** @type {HTMLElement|null} */ (this.querySelector('[data-blog-pagination]'));
      this.#loadedUpToPage = Number(paginationBlock?.dataset.currentPage) || 1;

      if (this.hasAttribute('data-load-more-enabled')) {
        this.#initLoadMore();
      }
    }
  }

  #initLoadMore() {
    const button = this.querySelector('[data-load-more-container] button');
    if (!button) return;

    button.addEventListener('click', () => this.#handleLoadMore());
  }

  async #handleLoadMore() {
    const paginationBlock = /** @type {HTMLElement|null} */ (this.querySelector('[data-blog-pagination]'));
    const totalPages = Number(paginationBlock?.dataset.totalPages) || 1;

    if (this.#loadedUpToPage >= totalPages) return;

    const button = /** @type {HTMLElement|null} */ (this.querySelector('[data-load-more-container] button'));
    button?.classList.add('sga-button--loading');
    button?.setAttribute('disabled', '');

    await this.loadNextPage();
    this.#loadedUpToPage++;

    this.#updatePaginationUI();
    this.#updateArrows(totalPages);

    if (this.#loadedUpToPage >= totalPages) {
      // Hide load more when all pages loaded
      this.querySelector('[data-load-more-container]')?.remove();
    } else {
      button?.classList.remove('sga-button--loading');
      button?.removeAttribute('disabled');
    }
  }

  /**
   * @param {number} pageNum
   * @returns {string}
   */
  #pageNumberHref(pageNum) {
    const refLink = /** @type {HTMLAnchorElement|null} */ (
      this.querySelector('a[data-page-item][href*="page="]')
    );
    if (refLink) {
      const url = new URL(refLink.href, window.location.origin);
      url.searchParams.set('page', String(pageNum));
      return `${url.pathname}${url.search}`;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNum));
    return `${url.pathname}${url.search}`;
  }

  /**
   * @param {number} totalPages
   */
  #updateArrows(totalPages) {
    // Prev arrow: enable once we've moved past page 1
    if (this.#loadedUpToPage > 1) {
      const prevArrow = this.querySelector('button[data-pagination-prev]');
      if (prevArrow) {
        const link = document.createElement('a');
        link.href = this.#pageNumberHref(this.#loadedUpToPage - 1);
        link.className = 'sga-blog-pagination__item sga-blog-pagination__arrow';
        link.setAttribute('data-pagination-prev', '');
        link.setAttribute('aria-label', prevArrow.getAttribute('aria-label') || '');
        link.innerHTML = prevArrow.innerHTML;
        prevArrow.replaceWith(link);
      }
    }

    // Next arrow: disable once all pages are loaded
    if (this.#loadedUpToPage >= totalPages) {
      const nextArrow = this.querySelector('a[data-pagination-next]');
      if (nextArrow) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sga-blog-pagination__item sga-blog-pagination__arrow sga-blog-pagination__arrow--disabled';
        btn.setAttribute('data-pagination-next', '');
        btn.setAttribute('aria-label', nextArrow.getAttribute('aria-label') || '');
        btn.setAttribute('disabled', '');
        btn.innerHTML = nextArrow.innerHTML;
        nextArrow.replaceWith(btn);
      }
    }
  }

  #updatePaginationUI() {
    const items = this.querySelectorAll('[data-page-item]');
    /** @type {Element|null} */
    let targetItem = null;

    for (const item of items) {
      const pageNum = parseInt(item.textContent.trim(), 10);
      if (pageNum === this.#loadedUpToPage) {
        targetItem = item;
        break;
      }
    }

    if (!targetItem) return;

    const currentActive = this.querySelector('[data-page-item-active]');

    if (currentActive && currentActive !== targetItem) {
      currentActive.classList.remove('sga-blog-pagination__item--active');
      currentActive.removeAttribute('data-page-item-active');
      currentActive.removeAttribute('aria-current');

      if (currentActive.tagName === 'SPAN') {
        const prevPage = parseInt(currentActive.textContent.trim(), 10);
        if (!Number.isNaN(prevPage)) {
          const link = document.createElement('a');
          link.href = this.#pageNumberHref(prevPage);
          link.className = 'sga-blog-pagination__item';
          link.setAttribute('data-page-item', '');
          link.textContent = currentActive.textContent.trim();
          currentActive.replaceWith(link);
        }
      }
    }

    if (targetItem.tagName === 'A') {
      const span = document.createElement('span');
      span.className = 'sga-blog-pagination__item sga-blog-pagination__item--active';
      span.setAttribute('data-page-item', '');
      span.setAttribute('data-page-item-active', '');
      span.setAttribute('aria-current', 'page');
      span.textContent = targetItem.textContent.trim();
      targetItem.replaceWith(span);
    } else {
      targetItem.classList.add('sga-blog-pagination__item--active');
      targetItem.setAttribute('data-page-item-active', '');
      targetItem.setAttribute('aria-current', 'page');
    }
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
